import React, { useState } from 'react';
import { FormikProps } from 'formik';
import classNameBind from 'classnames/bind';
import { TFunction } from 'react-i18next';
import {
  Form,
  Stack,
  TextFieldControl,
  Text,
  Link,
  withTranslation,
  Card,
  Information,
} from '@piwikpro/ui-components';
import { AuthSettings } from '@piwikpro/auth-crate';
import { FormValidator } from '@piwikpro/form-crate';
import TitleComponent from '@piwikpro/title-crate/Title';
import { withInject } from '@piwikpro/platform';
import { AutoSavedVerificationCodeInputControl } from '../../VerificationCodeInput';
import styles from './TwoFactorConfirmCurrent.module.css';

const bindClasses = classNameBind.bind(styles);

interface IFormValues {
  code: number
}

interface InjectedProps {
  authService: AuthSettings
  validation: FormValidator
  Title: typeof TitleComponent
}

interface StateProps {
  failed?: boolean
  twoFactorAuthEnforced?: boolean
}

interface Props extends StateProps, InjectedProps {
  setCurrentCodeViewVisible: (status: boolean) => void
  t: TFunction
}

const TwoFactorConfirmCurrent: React.FC<Props> = ({
  authService,
  validation,
  Title,
  failed,
  twoFactorAuthEnforced,
  setCurrentCodeViewVisible,
  t,
}) => {
  const [backupCodesViewVisible, setBackupCodesViewVisible] = useState(false);
  const handleSubmit = async (values: IFormValues, api: FormikProps<IFormValues>) => {
    try {
      await authService.setup2faForCurrentUser({ code: String(values.code) });
      setCurrentCodeViewVisible(false);
    // eslint-disable-next-line no-empty
    } catch {} finally {
      api.setFieldValue('code', '');
      api.setSubmitting(false);
    }
  };

  return (
    <Title part={t(twoFactorAuthEnforced ? 'security.2fa-reset.title' : 'security.confirmCurrentModal.title')} subset>
      <div className={bindClasses('wrapper')}>
        <Card.Section>
          <Form
            id="two-factor-disable-form"
            validationSchema={validation.createValidationSchema(
              () => ({
                code: validation
                  .getYup()
                  .string()
                  .min(8, t('security.backupCode.validation'))
                  .max(8, t('security.backupCode.validation')),
              }),
            )}
            initialValues={{
              code: '',
            }}
            onSubmit={handleSubmit}
            render={({ isSubmitting, resetForm, setFieldValue }: FormikProps<IFormValues>) => (
              <Stack vertical horizontalAlignment="middle">
                <Stack.Item>
                  <Text as="h3" size={16} weight={700} wrapType="noWrapAndCutText" dataId="Header.small">
                    {t('security.confirmCurrentModal.header')}
                  </Text>
                </Stack.Item>
                <Stack.Item fill>
                  <Text as="p">
                    {backupCodesViewVisible
                      ? t('security.confirmCurrentModal.backupCode.info')
                      : t('security.confirmCurrentModal.commonCode.info')
                    }
                  </Text>
                </Stack.Item>
                {failed && (
                  <Stack.Item fill>
                    <Information type="error">
                      {t('security.invalidAuthCode.info')}
                    </Information>
                  </Stack.Item>
                )}
                <Stack.Item fill>
                  {backupCodesViewVisible
                    ? (
                      <Stack spacing="narrow" horizontalAlignment="middle">
                        <Stack.Item>
                          <div className={bindClasses('backup-codes-input')}>
                            <TextFieldControl
                              name="code"
                              type="tel"
                              onChange={(e) => {
                                const numericValue = (e.target.value as string).replace(/[^0-9]/g, '');
                                setFieldValue('code', numericValue);
                              }}
                              placeholder={t('security.confirmCurrentModal.backupCode.placeholder')}
                              autoFocus
                            />
                          </div>
                        </Stack.Item>
                        <Stack.Item>
                          <Form.Submit text={t('security.confirmCurrentModal.backupCode.verifyBtn.text')} />
                        </Stack.Item>
                      </Stack>
                    ) : (
                      <AutoSavedVerificationCodeInputControl
                        name="code"
                        initialValue=""
                        backupCodesLinkText={t('security.confirmCurrentModal.commonCode.link.text')}
                        onBackupCodesLinkClick={() => {
                          resetForm();
                          authService.reinitializeSetup2faForCurrentUser();
                          setBackupCodesViewVisible(true);
                        }}
                        validationSchema={validation.createValidationSchema(
                          () => ({
                            code: validation
                              .getYup()
                              .string()
                              .min(6),
                          }),
                        )}
                        onSubmit={handleSubmit}
                        withRightSideSpinner
                      />
                    )}
                </Stack.Item>
                {backupCodesViewVisible && (
                  <Stack.Item fill>
                    <div
                      className={bindClasses('link', { isSubmitting })}
                    >
                      <Link
                        size="small"
                        onClick={() => {
                          resetForm();
                          authService.reinitializeSetup2faForCurrentUser();
                          setBackupCodesViewVisible(false);
                        }}
                      >
                        {t('security.confirmCurrentModal.backupCode.link.text')}
                      </Link>
                    </div>
                  </Stack.Item>
                )}
              </Stack>
            )}
          />
        </Card.Section>
      </div>
    </Title>
  );
};

export default withInject<InjectedProps>({
  authService: 'AuthCrate.authSettings',
  validation: 'FormCrate.validation',
  Title: 'TitleCrate.components.Title',
})(withTranslation('profile')(TwoFactorConfirmCurrent));
